import { useCallback, useEffect, useRef } from 'react'

interface NavigationRefs {
  contextControl: React.RefObject<HTMLButtonElement>
  textarea: React.RefObject<HTMLTextAreaElement>
  attachmentButton: React.RefObject<HTMLButtonElement>
  moreButton: React.RefObject<HTMLButtonElement>
  sendButton: React.RefObject<HTMLButtonElement>
  contextPills: React.MutableRefObject<HTMLButtonElement[]>
}

export const useArrowKeyNavigation = (
  containerRef: React.RefObject<HTMLDivElement>,
  onRemoveContextItem?: (value: string) => void
) => {
  const contextControlRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const attachmentButtonRef = useRef<HTMLButtonElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const sendButtonRef = useRef<HTMLButtonElement>(null)
  const contextPillsRef = useRef<HTMLButtonElement[]>([])

  const refs: NavigationRefs = {
    contextControl: contextControlRef,
    textarea: textareaRef,
    attachmentButton: attachmentButtonRef,
    moreButton: moreButtonRef,
    sendButton: sendButtonRef,
    contextPills: contextPillsRef
  }

  // Check if textarea cursor is at top line
  const isTextareaAtTop = useCallback(() => {
    if (!textareaRef.current) return true

    const textarea = textareaRef.current
    const cursorPosition = textarea.selectionStart
    const textBeforeCursor = textarea.value.substring(0, cursorPosition)

    return !textBeforeCursor.includes('\n')
  }, [])

  // Check if textarea cursor is at bottom line
  const isTextareaAtBottom = useCallback(() => {
    if (!textareaRef.current) return true

    const textarea = textareaRef.current
    const cursorPosition = textarea.selectionEnd
    const textAfterCursor = textarea.value.substring(cursorPosition)

    return !textAfterCursor.includes('\n')
  }, [])

  // Get context pills as a flat array
  const getContextPills = useCallback(() => {
    return contextPillsRef.current || []
  }, [])

  // Check if element is a context pill
  const isContextPill = useCallback((element: Element) => {
    const pills = contextPillsRef.current || []
    return pills.includes(element as HTMLButtonElement)
  }, [])

  // Get index of context pill
  const getContextPillIndex = useCallback((element: Element) => {
    const pills = contextPillsRef.current || []
    return pills.findIndex(pill => pill === element)
  }, [])

  // Handle arrow down navigation
  const handleArrowDown = useCallback(
    (focusedElement: Element) => {
      if (focusedElement === contextControlRef.current) {
        const pills = contextPillsRef.current || []
        if (pills.length > 0) {
          return pills[0]
        }
        return textareaRef.current
      }

      const pills = contextPillsRef.current || []
      if (pills.includes(focusedElement as HTMLButtonElement)) {
        return textareaRef.current
      }

      if (focusedElement === textareaRef.current && isTextareaAtBottom()) {
        return attachmentButtonRef.current
      }

      // Bottom row buttons stay in place for down arrow
      return null
    },
    [isTextareaAtBottom]
  )

  // Handle arrow up navigation
  const handleArrowUp = useCallback(
    (focusedElement: Element) => {
      if (focusedElement === textareaRef.current && isTextareaAtTop()) {
        const pills = contextPillsRef.current || []
        if (pills.length > 0) {
          return pills[pills.length - 1] // Focus last pill
        }
        return contextControlRef.current
      }

      const pills = contextPillsRef.current || []
      if (pills.includes(focusedElement as HTMLButtonElement)) {
        return contextControlRef.current
      }

      // Any bottom button goes up to textarea
      if (
        focusedElement === attachmentButtonRef.current ||
        focusedElement === moreButtonRef.current ||
        focusedElement === sendButtonRef.current
      ) {
        return textareaRef.current
      }

      return null
    },
    [isTextareaAtTop]
  )

  // Handle arrow left navigation
  const handleArrowLeft = useCallback((focusedElement: Element) => {
    const pills = contextPillsRef.current || []
    if (pills.includes(focusedElement as HTMLButtonElement)) {
      const index = pills.findIndex(pill => pill === focusedElement)
      if (index > 0) {
        return pills[index - 1]
      }
      return contextControlRef.current
    }

    if (focusedElement === moreButtonRef.current) {
      return attachmentButtonRef.current
    }

    if (focusedElement === sendButtonRef.current) {
      return moreButtonRef.current
    }

    return null
  }, [])

  // Handle arrow right navigation
  const handleArrowRight = useCallback((focusedElement: Element) => {
    if (focusedElement === contextControlRef.current) {
      const pills = contextPillsRef.current || []
      if (pills.length > 0) {
        return pills[0]
      }
      return null
    }

    const pills = contextPillsRef.current || []
    if (pills.includes(focusedElement as HTMLButtonElement)) {
      const index = pills.findIndex(pill => pill === focusedElement)
      if (index < pills.length - 1) {
        return pills[index + 1]
      }
      return null
    }

    if (focusedElement === attachmentButtonRef.current) {
      return moreButtonRef.current
    }

    if (focusedElement === moreButtonRef.current) {
      return sendButtonRef.current
    }

    return null
  }, [])

  // Main arrow key navigation handler
  const handleArrowKeyNavigation = useCallback(
    (e: KeyboardEvent) => {
      const focusedElement = document.activeElement

      // Early return if focus is not within our container
      if (!containerRef.current?.contains(focusedElement)) return
      if (!focusedElement) return

      // Handle backspace for context pills
      const pills = contextPillsRef.current || []
      if (
        e.key === 'Backspace' &&
        pills.includes(focusedElement as HTMLButtonElement) &&
        onRemoveContextItem
      ) {
        const pillValue = (focusedElement as HTMLButtonElement).getAttribute(
          'data-value'
        )
        if (pillValue) {
          e.preventDefault()
          const currentIndex = pills.findIndex(pill => pill === focusedElement)
          onRemoveContextItem(pillValue)

          // Smart focus after removal
          setTimeout(() => {
            const updatedPills = contextPillsRef.current || []
            if (updatedPills.length > 0) {
              // If there are still pills, focus the next one or the last one if we deleted the last
              const nextIndex =
                currentIndex < updatedPills.length
                  ? currentIndex
                  : updatedPills.length - 1
              updatedPills[nextIndex]?.focus()
            } else {
              // No pills left, focus the context control
              contextControlRef.current?.focus()
            }
          }, 0)
          return
        }
      }

      let nextElement: HTMLElement | null = null

      // Determine next element based on arrow key
      switch (e.key) {
        case 'ArrowDown':
          nextElement = handleArrowDown(focusedElement)
          break
        case 'ArrowUp':
          nextElement = handleArrowUp(focusedElement)
          break
        case 'ArrowLeft':
          nextElement = handleArrowLeft(focusedElement)
          break
        case 'ArrowRight':
          nextElement = handleArrowRight(focusedElement)
          break
        default:
          return // Early return for non-arrow keys
      }

      // Early return if no next element to focus
      if (!nextElement) return

      // Prevent default and focus next element
      e.preventDefault()
      nextElement.focus()
    },
    [
      containerRef,
      handleArrowDown,
      handleArrowUp,
      handleArrowLeft,
      handleArrowRight,
      onRemoveContextItem
    ]
  )

  // Set up keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleArrowKeyNavigation)
    return () =>
      document.removeEventListener('keydown', handleArrowKeyNavigation)
  }, [handleArrowKeyNavigation])

  return refs
}
